import { ChevronDownIcon, Trash2Icon, ZoomInIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Input } from '@app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@app/components/ui/select'
import { cn } from '@app/lib/cn'
import type { BomLineOption, SayaMaterialDto } from './api'
import { HANDEDNESS_VALUES, ORIENTATION_VALUES } from './api'
import {
  bomLineGroups,
  bomLineKey,
  bomLineLabel,
  bomRefFromKey,
  bomRefKey,
  directionWarning,
  formatDesi,
  HANDEDNESS_LABELS,
  MAX_QTY_PER_PAIR,
  MIN_QTY_PER_PAIR,
  mm2ToDesi,
  NO_BOM_VALUE,
  ORIENTATION_LABELS,
  PURCHASE_UNIT_LABELS,
  type PartFormValues,
} from './format'
import {
  NO_SUGGESTED_FIELDS,
  SUGGESTION_CHIP_TEXT,
  SUGGESTION_CHIP_TITLE,
  type SuggestedFields,
} from './suggest'

/**
 * The piece rail — one ROW per traced shape, not one card.
 *
 * It used to be a stack of open forms, six fields deep each, on the argument
 * that a field you have to expand is a field you will not check. The factory
 * settled that argument the other way: a set is a dozen pieces, six open forms
 * is a screenful, and an operator who has to scroll to reach piece 7 cannot see
 * the scan while they do it. So the row carries exactly what gets touched on
 * every piece — number, name, material, adet, göz onayı — and the three fields
 * that are right by default (el kuralı, yön, BOM satırı) fold behind a chevron.
 *
 * The number is the same number the canvas draws on the shape, which is still
 * the only link between the two halves of the screen; hovering a row rings that
 * shape, clicking the shape selects the row.
 */

export interface PieceEntry {
  key: string
  values: PartFormValues
  netMm2: number
  /**
   * Both rails pass the shared sheet-prefixed numeral used by the strips
   * ("2.3"); the positional fallback only covers callers that supply none.
   */
  label?: string
  /**
   * Quality remarks about this piece, worded for a human and shown only when
   * the row is opened. All of them are informational — none refuses a save or
   * an approval — so they read in grey, where somebody looking into a piece
   * will find them.
   */
  notes: string[]
  /**
   * Controls the AI pre-fill wrote and nobody has checked yet. Each one draws
   * its own "öneri" chip, and the chip goes as soon as a human edits that
   * field — see `clearSuggested`.
   */
  suggested?: SuggestedFields
}

/**
 * One tarama's worth of rows in the set-wide rail. The header is small on
 * purpose: it locates, the rows are still the work.
 */
export interface PieceRailGroup {
  key: string
  /** "Tarama 2" — drawn as a small header above the group's rows. */
  label: string | null
  pieces: readonly PieceEntry[]
}

export interface PieceRailProps {
  pieces: readonly PieceEntry[]
  /**
   * Set-wide mode: every tarama's pieces, grouped under per-tarama headers.
   * When present it replaces `pieces` as what is drawn; the callbacks are
   * key-routed either way, so the caller decides the scope, not the rail.
   */
  groups?: readonly PieceRailGroup[]
  materials: readonly SayaMaterialDto[]
  bomLines: readonly BomLineOption[]
  activeKey: string | null
  onActivate: (key: string) => void
  onChange: (key: string, patch: Partial<PartFormValues>) => void
  /** Takes the piece out of the set here and now; the next Kaydet persists it. */
  onDelete?: (key: string) => void
  /** The row under the cursor, so the canvas can ring the shape it belongs to. */
  onHover?: (key: string | null) => void
  /**
   * Zoom the canvas onto this piece. Offered on the SELECTED row only — the
   * gesture reads "closer on this one", and a zoom glyph on every row would
   * bury the one that means it.
   */
  onZoom?: (key: string) => void
  /** An approved revision is read-only — its geometry is immutable. */
  disabled?: boolean
}

export function PieceRail({
  pieces,
  groups,
  materials,
  bomLines,
  activeKey,
  onActivate,
  onChange,
  onDelete,
  onHover,
  onZoom,
  disabled = false,
}: PieceRailProps) {
  // Split once for the whole rail: every row offers the same option list.
  const bomGroups = useMemo(() => bomLineGroups(bomLines), [bomLines])
  const sections: readonly PieceRailGroup[] = groups ?? [{ key: 'rail', label: null, pieces }]

  return (
    <div onMouseLeave={() => onHover?.(null)}>
      {sections.map((section) => (
        <section key={section.key} aria-label={section.label ?? undefined}>
          {section.label ? (
            <p className="eyebrow border-b border-hairline bg-kagit/60 px-2 pb-1 pt-2">
              {section.label}
            </p>
          ) : null}
          <ol className="flex flex-col">
            {section.pieces.map((piece, index) => (
              <PieceRow
                key={piece.key}
                number={piece.label ?? String(index + 1)}
                piece={piece}
                materials={materials}
                bomLines={bomLines}
                bomGroups={bomGroups}
                active={piece.key === activeKey}
                onActivate={() => onActivate(piece.key)}
                onChange={(patch) => onChange(piece.key, patch)}
                onDelete={onDelete ? () => onDelete(piece.key) : undefined}
                onHover={onHover ? (over) => onHover(over ? piece.key : null) : undefined}
                onZoom={onZoom ? () => onZoom(piece.key) : undefined}
                disabled={disabled}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  )
}

function PieceRow({
  number,
  piece,
  materials,
  bomLines,
  bomGroups,
  active,
  onActivate,
  onChange,
  onDelete,
  onHover,
  onZoom,
  disabled,
}: {
  number: string
  piece: PieceEntry
  materials: readonly SayaMaterialDto[]
  bomLines: readonly BomLineOption[]
  bomGroups: { cutting: BomLineOption[]; other: BomLineOption[] }
  active: boolean
  onActivate: () => void
  onChange: (patch: Partial<PartFormValues>) => void
  onDelete?: () => void
  onHover?: (over: boolean) => void
  onZoom?: () => void
  disabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const row = useRef<HTMLLIElement | null>(null)
  const { values } = piece
  const fieldId = `saya-part-${piece.key}`
  const material = materials.find((candidate) => candidate.id === values.materialId)
  const napWarning = directionWarning(material, values.orientation)
  const { cutting, other } = bomGroups
  const suggested = piece.suggested ?? NO_SUGGESTED_FIELDS

  // Selecting a piece ON THE CANVAS has to bring its row into view, or the
  // click looks like it did nothing.
  useEffect(() => {
    if (active) row.current?.scrollIntoView({ block: 'nearest' })
  }, [active])

  return (
    <li
      ref={row}
      onFocusCapture={onActivate}
      onMouseEnter={() => onHover?.(true)}
      className={cn(
        'border-b border-hairline last:border-b-0',
        active && 'bg-kordovan/5',
      )}
    >
      <div className="flex items-center gap-1.5 px-2 py-1">
        <button
          type="button"
          onClick={onActivate}
          aria-label={`${number}. parçayı seç`}
          className={cn(
            // min-width, not a fixed square: the set-wide rail numbers rows
            // "2.3", and a numeral clipped to its first digit points at the
            // wrong piece.
            'grid h-5 min-w-5 shrink-0 place-items-center rounded-xs px-0.5 font-mono text-micro font-semibold tnum transition-colors',
            active ? 'bg-kordovan text-kagit' : 'bg-kordovan/10 text-kordovan hover:bg-kordovan/20',
          )}
        >
          {number}
        </button>

        <input
          id={`${fieldId}-name`}
          value={values.name}
          onChange={(event) => onChange({ name: event.target.value })}
          disabled={disabled}
          placeholder="Adsız parça"
          aria-label={`${number}. parçanın adı`}
          aria-invalid={values.name.trim() === '' ? true : undefined}
          className={cn(
            'h-7 min-w-0 flex-1 rounded-xs border border-transparent bg-transparent px-1.5 text-data text-ink',
            'transition-colors placeholder:text-ink-muted/70 hover:border-hairline',
            'focus-visible:border-kordovan focus-visible:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kordovan/25',
            'disabled:cursor-not-allowed disabled:opacity-60',
            'aria-[invalid=true]:border-hardal/50',
          )}
        />

        <span className="hidden w-12 shrink-0 text-right font-mono text-micro tnum text-ink-muted xl:block">
          {formatDesi(mm2ToDesi(piece.netMm2))}
        </span>

        <span className="relative shrink-0">
          <Select
            value={values.materialId == null ? undefined : String(values.materialId)}
            onValueChange={(value) => onChange({ materialId: Number(value) })}
            disabled={disabled}
          >
            <SelectTrigger
              id={`${fieldId}-material`}
              aria-label={`${number}. parçanın malzemesi`}
              aria-invalid={values.materialId == null ? true : undefined}
              className={cn(
                'h-7 w-24 gap-1 px-1.5 text-micro [&>span]:truncate',
                values.materialId == null && 'border-hardal/40',
              )}
            >
              <SelectValue placeholder="Malzeme" />
            </SelectTrigger>
            <SelectContent>
              {materials.map((option) => (
                <SelectItem key={option.id} value={String(option.id)}>
                  {option.name} · {PURCHASE_UNIT_LABELS[option.purchaseUnit]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {suggested.material ? <SuggestionDot /> : null}
        </span>

        <span className="relative shrink-0">
          <CountInput
            id={`${fieldId}-qty`}
            label={`${number}. parçanın adedi`}
            value={values.qtyPerPair}
            onChange={(qtyPerPair) => onChange({ qtyPerPair })}
            disabled={disabled}
          />
          {suggested.qty ? <SuggestionDot /> : null}
        </span>

        {onZoom ? (
          active ? (
            <button
              type="button"
              onClick={onZoom}
              title="Yakınlaştır"
              className="grid size-6 shrink-0 place-items-center rounded-xs text-ink-muted transition-colors hover:bg-kordovan/8 hover:text-ink"
            >
              <ZoomInIcon className="size-3.5" aria-hidden />
              <span className="sr-only">{number}. parçayı yakınlaştır</span>
            </button>
          ) : (
            // Reserved slot: the zoom affordance appearing on the active row
            // must not shove every control on that row leftward.
            <span aria-hidden className="size-6 shrink-0" />
          )
        ) : null}

        <button
          type="button"
          onClick={() => setOpen((previous) => !previous)}
          aria-expanded={open}
          aria-controls={`${fieldId}-more`}
          title="El kuralı, yön ve BOM satırı"
          className="grid size-6 shrink-0 place-items-center rounded-xs text-ink-muted transition-colors hover:bg-kordovan/8 hover:text-ink"
        >
          <ChevronDownIcon
            className={cn('size-3.5 transition-transform', open && 'rotate-180')}
            aria-hidden
          />
          <span className="sr-only">{number}. parçanın diğer alanları</span>
        </button>

        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            title="Parçayı sil"
            className="grid size-6 shrink-0 place-items-center rounded-xs text-ink-muted transition-colors hover:bg-kordovan/8 hover:text-kordovan"
          >
            <Trash2Icon className="size-3.5" aria-hidden />
            <span className="sr-only">{number}. parçayı sil</span>
          </button>
        ) : null}
      </div>

      {open ? (
        <div id={`${fieldId}-more`} className="grid gap-2 px-2 pb-2 pl-8 sm:grid-cols-3">
          <Field label="El kuralı" htmlFor={`${fieldId}-handedness`}>
            <Select
              value={values.handedness}
              onValueChange={(value) =>
                onChange({ handedness: value as PartFormValues['handedness'] })
              }
              disabled={disabled}
            >
              <SelectTrigger id={`${fieldId}-handedness`} className="h-7 px-1.5 text-micro">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HANDEDNESS_VALUES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {HANDEDNESS_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Yön" htmlFor={`${fieldId}-orientation`}>
            <Select
              value={values.orientation}
              onValueChange={(value) =>
                onChange({ orientation: value as PartFormValues['orientation'] })
              }
              disabled={disabled}
            >
              <SelectTrigger id={`${fieldId}-orientation`} className="h-7 px-1.5 text-micro">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORIENTATION_VALUES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {ORIENTATION_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="BOM satırı" htmlFor={`${fieldId}-bom`}>
            <Select
              // Unanswered shows the placeholder, NOT "BOM'da yok": that answer
              // is a real statement about Korgun's recipe and must be chosen,
              // never inherited from a part nobody looked at.
              value={values.bomAnswered ? bomRefKey(values.bomRef) : undefined}
              onValueChange={(value) =>
                onChange({
                  bomRef: value === NO_BOM_VALUE ? null : bomRefFromKey(value, bomLines),
                  bomAnswered: true,
                })
              }
              disabled={disabled}
            >
              <SelectTrigger
                id={`${fieldId}-bom`}
                className="h-7 px-1.5 text-micro [&>span]:truncate"
              >
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent>
                {cutting.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Kesim satırları</SelectLabel>
                    {cutting.map((line) => (
                      <SelectItem key={bomLineKey(line)} value={bomLineKey(line)}>
                        {bomLineLabel(line)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
                {other.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Diğer prosesler</SelectLabel>
                    {other.map((line) => (
                      <SelectItem key={bomLineKey(line)} value={bomLineKey(line)}>
                        {bomLineLabel(line)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
                {bomLines.length > 0 ? <SelectSeparator /> : null}
                <SelectItem value={NO_BOM_VALUE}>BOM&apos;da yok</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <p className="text-micro text-ink-muted sm:col-span-3">
            {formatDesi(mm2ToDesi(piece.netMm2))} desi net
            {napWarning ? <span className="text-hardal"> · {napWarning}</span> : null}
            {!values.bomAnswered ? ' · BOM satırını seçin, gerçekten yoksa “BOM’da yok”.' : ''}
          </p>

          {piece.notes.map((note) => (
            <p key={note} className="text-micro text-ink-muted sm:col-span-3">
              {note}
            </p>
          ))}
        </div>
      ) : null}
    </li>
  )
}

/**
 * "This one was filled in for you." Deliberately the quietest thing in the row.
 *
 * It sits beside the control it describes rather than on the row, because the
 * claim is about one field: a piece can perfectly well carry a material the
 * operator chose next to an adet a machine guessed. It disappears the moment
 * that field is edited, so a chip on screen always means the same thing —
 * nobody has looked at this yet.
 */
/**
 * AI-prefill marker as a corner dot rather than an inline chip: the chip's
 * width made the controls zig-zag between rows that carried suggestions and
 * rows that did not — the one thing a column of forms must never do. The dot
 * annotates the control it sits on and costs the row no width at all.
 */
function SuggestionDot() {
  return (
    <span
      title={SUGGESTION_CHIP_TITLE}
      className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-celik ring-2 ring-surface"
    >
      <span className="sr-only">{SUGGESTION_CHIP_TEXT}</span>
    </span>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <label htmlFor={htmlFor} className="text-micro font-medium text-ink-muted">
        {label}
      </label>
      {children}
    </div>
  )
}

/**
 * The quantity box, which cannot hold a value the API would refuse.
 *
 * The text is local so the field can be EMPTY mid-edit — clearing it to type
 * "12" used to snap the value to 0, which then travelled to the server on the
 * next save and came back as "Geçersiz istek gövdesi." with no clue which piece
 * it meant. Only a whole number in range is reported upwards; anything else
 * simply is not committed, and leaving the field restores the last good value.
 */
export function CountInput({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string
  label: string
  value: number
  onChange: (value: number) => void
  disabled: boolean
}) {
  const [text, setText] = useState(() => String(value))

  useEffect(() => {
    setText(String(value))
  }, [value])

  return (
    <Input
      id={id}
      type="number"
      inputMode="numeric"
      aria-label={label}
      title="Adet / çift"
      min={MIN_QTY_PER_PAIR}
      max={MAX_QTY_PER_PAIR}
      step={1}
      mono
      value={text}
      disabled={disabled}
      // No spinner: at this width the arrows would eat the digits, and the
      // value is two characters that get typed, not stepped.
      className="h-7 w-11 shrink-0 px-1 text-center text-micro [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      onChange={(event) => {
        const raw = event.target.value
        setText(raw)
        const parsed = Number.parseInt(raw, 10)
        if (
          String(parsed) === raw.trim() &&
          parsed >= MIN_QTY_PER_PAIR &&
          parsed <= MAX_QTY_PER_PAIR
        ) {
          onChange(parsed)
        }
      }}
      onBlur={() => setText(String(value))}
    />
  )
}
